/*
 * usf-quick-search.js
 *
 * Feeds the theme's own predictive-search dropdown with the product ranking from the
 * AI Search & Product Filter (USF / Sobooster) app, so the header search modal agrees with the
 * /search results page instead of contradicting it.
 *
 * Why this exists: predictive-search.js queries Shopify's native /search/suggest, whose relevance
 * model is unrelated to the app's. Re-sorting its payload cannot help, because the products the
 * app ranks first are often far outside Shopify's top-N and never reach the browser. So the data
 * source is swapped while every bit of the theme's markup, CSS, animation and dropdown behaviour
 * is preserved: the cards are still rendered by the theme itself, server-side, via
 * ?view=ajax_quick_search_card, and the surrounding shell comes from the Liquid <template> in
 * snippets/predictive-search-results.liquid.
 *
 * Contract with the app, read off InstantSearchBase.updateResults in assets/usf.js:
 *
 *   GET {usf.settings.searchSvcUrl}instantsearch
 *       q, apiKey={usf.settings.siteId}, take, [skip], [sort], [customerTags], [country], [locale]
 *   -> { data: { items: [ { urlName, id, title, variants, ... } ], total, query, currency } }
 *
 * Two details matter:
 *   - `sort` is deliberately omitted. That is what makes the service fall back to its relevance
 *     ranking, which is what the app's search page shows under "Pertinence". Sending sort=r would
 *     be wrong; usf.js treats 'r' as "no sort parameter".
 *   - `urlName` is the product handle, not `handle` (see usf.platform.getProductUrl in usf-boot.js).
 *
 * If the app is unreachable, unconfigured, or the Liquid template is missing, every path falls
 * back to the untouched native implementation rather than leaving the user with a dead dropdown.
 *
 * Loaded unminified and after predictive-search.js from snippets/global-script.liquid.
 */
(function () {
  'use strict';

  var CARD_VIEW = 'ajax_quick_search_card';
  var TEMPLATE_SELECTOR = '[data-qs-results-template]';

  // handle -> card HTML. Cards do not change between keystrokes, so one product fetched for
  // "bagu" is reused for "bague"; PredictiveSearch.cachedResults separately caches whole dropdowns.
  var cardCache = {};

  function appSettings() {
    return (window.usf && window.usf.settings) || null;
  }

  function serviceUrl() {
    var settings = appSettings();
    if (!settings || !settings.searchSvcUrl || !settings.siteId) return null;
    return settings.searchSvcUrl + 'instantsearch';
  }

  // usf.platform exposes country/locale/customerTags as getters that reach into Shopify.* and
  // globals set by snippets/usf.liquid; any of them can be absent depending on the page.
  function platformValue(key) {
    try {
      var platform = window.usf && window.usf.platform;
      return platform ? platform[key] : null;
    } catch (error) {
      return null;
    }
  }

  function rankingUrl(term, take) {
    var params = new URLSearchParams();

    params.set('q', term);
    params.set('apiKey', appSettings().siteId);
    params.set('take', String(take));

    // usf.platform is defined by usf-boot.js, but fall back to the globals snippets/usf.liquid
    // sets directly: locale in particular decides which language's catalogue is ranked, and this
    // store runs six of them.
    var customerTags = platformValue('customerTags') || window._usfCustomerTags;
    if (customerTags && customerTags.length) params.set('customerTags', customerTags.join(','));

    var country = platformValue('country') || (window.Shopify && window.Shopify.country);
    if (country) params.set('country', country);

    var locale = platformValue('locale') || window._usfLocale;
    if (locale) params.set('locale', locale);

    return serviceUrl() + '?' + params.toString();
  }

  function fetchRanking(term, take) {
    return fetch(rankingUrl(term, take), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('usf instantsearch responded ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var data = (payload && payload.data) || {};
        var items = Array.isArray(data.items) ? data.items : [];
        var handles = [];

        items.forEach(function (item) {
          if (item && item.urlName && handles.indexOf(item.urlName) === -1) handles.push(item.urlName);
        });

        handles = handles.slice(0, take);

        return {
          handles: handles,
          total: typeof data.total === 'number' ? data.total : handles.length
        };
      });
  }

  function fetchCard(handle) {
    if (Object.prototype.hasOwnProperty.call(cardCache, handle)) {
      return Promise.resolve(cardCache[handle]);
    }

    var root = (window.routes && window.routes.root) || '';

    return fetch(root + '/products/' + encodeURIComponent(handle) + '?view=' + CARD_VIEW)
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .catch(function () {
        return '';
      })
      .then(function (html) {
        cardCache[handle] = html;
        return html;
      });
  }

  function searchPageUrl(term) {
    var base = (window.routes && window.routes.search_url) || '/search';
    return base + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last&type=product';
  }

  // The theme's own convention (predictive-search.js) is to inject the count into a literal "()"
  // in the translation. Support both that and an explicit [count] placeholder.
  function viewAllLabel(template, total) {
    var label = template.getAttribute('data-view-all-label') || '';

    return label.indexOf('[count]') !== -1
      ? label.replace('[count]', total)
      : label.replace('()', '(' + total + ')');
  }

  function noResultsLabel(template, term) {
    return (template.getAttribute('data-no-results-label') || '').replace('[terms]', term);
  }

  function cardElement(html) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector('.product');
  }

  /*
   * Clones the Liquid shell and fills it in, reproducing both states of
   * sections/predictive-search.liquid: with results the empty block is absent, and with none the
   * heading, grid and "view all" block are absent.
   */
  function buildResultsMarkup(template, term, cards, total) {
    var fragment = template.content.cloneNode(true);
    var grid = fragment.querySelector('[data-qs-products]');
    var empty = fragment.querySelector('[data-qs-empty]');
    var heading = fragment.querySelector('[data-qs-heading]');
    var viewAllWrapper = fragment.querySelector('[data-qs-view-all]');
    var viewAllLink = fragment.querySelector('[data-qs-view-all-link]');
    var liveRegion = fragment.querySelector('[data-predictive-search-live-region-count-value]');
    var rendered = 0;

    cards.forEach(function (html, index) {
      var card = cardElement(html);
      if (!card || !grid) return;

      // Only meaningful when settings.banner_animation == 'effect_fade_up', which is also the only
      // case where the Liquid wrapper carries scroll-trigger. Matches the section's stagger.
      if (card.classList.contains('scroll-trigger')) {
        card.style.setProperty('--animation-order', index + 1);
      }

      grid.appendChild(card);
      rendered++;
    });

    if (rendered === 0) {
      if (heading) heading.remove();
      if (grid) grid.remove();
      if (viewAllWrapper) viewAllWrapper.remove();

      if (empty) {
        empty.removeAttribute('hidden');

        var emptyLink = empty.querySelector('[data-qs-empty-link]');
        if (emptyLink) emptyLink.setAttribute('href', searchPageUrl(term));

        var emptyTerm = empty.querySelector('[data-qs-empty-term]');
        if (emptyTerm) emptyTerm.textContent = '"' + term + '"';
      }

      if (liveRegion) liveRegion.textContent = noResultsLabel(template, term);
    } else {
      if (empty) empty.remove();

      var label = viewAllLabel(template, total);

      if (viewAllLink) {
        viewAllLink.setAttribute('href', searchPageUrl(term));
        viewAllLink.textContent = label;
      }

      if (liveRegion) liveRegion.textContent = label;
    }

    var holder = document.createElement('div');
    holder.appendChild(fragment);

    return holder.innerHTML;
  }

  function install(PredictiveSearch) {
    if (!PredictiveSearch || PredictiveSearch.prototype.usfRankedSearchInstalled) return;
    PredictiveSearch.prototype.usfRankedSearchInstalled = true;

    var nativeGetSearchResults = PredictiveSearch.prototype.getSearchResults;

    PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
      var host = this;
      var template = this.querySelector(TEMPLATE_SELECTOR);

      if (!serviceUrl() || !template) {
        nativeGetSearchResults.call(this, searchTerm);
        return;
      }

      // Same cache key the native implementation uses, so behaviour is unchanged on repeat terms.
      var queryKey = searchTerm.replace(' ', '-').toLowerCase();

      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      var take = parseInt(this.productToShow, 12) || 12;

      // Guards against an earlier, slower response overwriting a later one.
      this.usfPendingTerm = searchTerm;

      fetchRanking(searchTerm, take)
        .then(function (ranking) {
          return Promise.all(ranking.handles.map(fetchCard)).then(function (cards) {
            return { ranking: ranking, cards: cards };
          });
        })
        .then(function (result) {
          if (host.usfPendingTerm !== searchTerm) return;

          var markup = buildResultsMarkup(template, searchTerm, result.cards, result.ranking.total);

          host.cachedResults[queryKey] = markup;
          host.renderSearchResults(markup);
        })
        .catch(function (error) {
          console.warn('[usf-quick-search] falling back to native predictive search:', error);
          nativeGetSearchResults.call(host, searchTerm);
        });
    };
  }

  if (window.customElements) {
    customElements.whenDefined('predictive-search').then(function () {
      install(customElements.get('predictive-search'));
    });
  }
})();
